/**
 * oauthRecovery.ts
 *
 * Google Identity Services (lazy-loaded from CDN) and Microsoft MSAL
 * (bundled via npm — no CDN dependency).
 */

import { PublicClientApplication } from '@azure/msal-browser';

export type OAuthProvider = 'google' | 'microsoft';

export interface OAuthIdentity {
    sub: string;       // Stable unique user ID from provider
    email: string;
    provider: OAuthProvider;
    id_token: string;  // Raw JWT — sent to server for verification
}

// ─── Google ────────────────────────────────────────────────────────────────

function loadGoogleScript(): Promise<void> {
    return new Promise((resolve, reject) => {
        if ((window as any).google?.accounts) return resolve();
        const existing = document.getElementById('gsi-script');
        if (existing) {
            existing.addEventListener('load', () => resolve());
            return;
        }
        const script = document.createElement('script');
        script.id = 'gsi-script';
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Failed to load Google Identity Services'));
        document.head.appendChild(script);
    });
}

export async function signInWithGoogle(clientId: string): Promise<OAuthIdentity> {
    await loadGoogleScript();
    const google = (window as any).google;

    return new Promise((resolve, reject) => {
        google.accounts.id.initialize({
            client_id: clientId,
            callback: (response: { credential: string }) => {
                if (!response.credential) {
                    return reject(new Error('No credential returned from Google'));
                }
                const payload = parseJwtPayload(response.credential);
                if (!payload?.sub || !payload?.email) {
                    return reject(new Error('Invalid Google ID token payload'));
                }
                resolve({
                    sub: payload.sub,
                    email: payload.email,
                    provider: 'google',
                    id_token: response.credential,
                });
            },
            auto_select: false,
            cancel_on_tap_outside: true,
        });

        // Trigger the One Tap UI
        google.accounts.id.prompt((notification: any) => {
            if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
                // Fallback: render a button and programmatically click it
                const container = document.createElement('div');
                container.id = '__google_btn_fallback';
                container.style.position = 'fixed';
                container.style.opacity = '0';
                container.style.pointerEvents = 'none';
                document.body.appendChild(container);

                google.accounts.id.renderButton(container, {
                    type: 'standard',
                    size: 'large',
                    theme: 'outline',
                });

                const btn = container.querySelector('div[role=button]') as HTMLElement;
                if (btn) {
                    btn.click();
                } else {
                    document.body.removeChild(container);
                    reject(new Error('Google sign-in prompt was dismissed or blocked'));
                }
            }
        });
    });
}

// ─── Microsoft ─────────────────────────────────────────────────────────────

export async function signInWithMicrosoft(
    clientId: string,
    tenantId: string = 'common'
): Promise<OAuthIdentity> {
    const msalInstance = new PublicClientApplication({
        auth: {
            clientId,
            authority: `https://login.microsoftonline.com/${tenantId}`,
            redirectUri: window.location.origin,
        },
        cache: {
            cacheLocation: 'sessionStorage',
            storeAuthStateInCookie: false,
        },
    });

    await msalInstance.initialize();

    // Race the popup against a 90s timeout so the UI never spins forever.
    // Most common cause of hangs: the redirect URI isn't registered in Azure AD.
    const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error(
            'Microsoft sign-in timed out. Make sure ' + window.location.hostname +
            ' is registered as a redirect URI in your Azure AD app (Authentication → Single-page application).'
        )), 90_000)
    );

    try {
        const response = await Promise.race([
            msalInstance.loginPopup({
                scopes: ['openid', 'profile', 'email'],
                prompt: 'select_account',
            }),
            timeoutPromise,
        ]);

        const claims = response.idTokenClaims as {
            sub?: string;
            oid?: string;
            preferred_username?: string;
            email?: string;
        } | undefined;
        if (!response.idToken || !(claims?.sub || claims?.oid)) {
            throw new Error('No ID token returned from Microsoft');
        }

        return {
            sub: (claims.sub || claims.oid)!,
            email: claims.preferred_username || claims.email || '',
            provider: 'microsoft',
            id_token: response.idToken,
        };
    } catch (err: any) {
        if (err.errorCode === 'user_cancelled') {
            throw new Error('Microsoft sign-in was cancelled');
        }
        throw err;
    }
}

// ─── Shared ─────────────────────────────────────────────────────────────────

/**
 * Decode a JWT payload without verifying the signature.
 * Signature verification happens on the server.
 */
export function parseJwtPayload(token: string): Record<string, any> | null {
    try {
        const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
        return JSON.parse(atob(base64));
    } catch {
        return null;
    }
}
